import { TextField } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslationContext } from '../../context/TranslationContext';
import { useGoogleMapsStatus } from './googleMapsUtils';

type LocationResult = {
  location: { lat: number; lng: number };
  address: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  streetName?: string;
  streetNumber?: string;
  shortAddress?: string;
};

type Props = {
  onPlaceSelected: (result: LocationResult) => void;
  onError?: (error: string) => void;
  placeholder?: string;
};

const SearchLocationInput = ({ onPlaceSelected, onError, placeholder }: Props) => {
  const t = useTranslationContext();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteRef = useRef<any>(null);
  const placeSelectedRef = useRef(onPlaceSelected);
  const errorRef = useRef(onError);

  const { isLoaded, isLoading, error: mapsError } = useGoogleMapsStatus();

  const currentPlaceholder = useMemo(() => {
    if (isLoading) return t('searchLocationModal.loadingGoogleMaps');
    if (mapsError) return t('searchLocationModal.googleMapsUnavailable');
    if (!isLoaded) return t('searchLocationModal.googleMapsNotLoaded');
    return placeholder ?? t('searchLocationModal.enterLocation');
  }, [isLoaded, isLoading, mapsError, placeholder, t]);

  useEffect(() => {
    placeSelectedRef.current = onPlaceSelected;
  }, [onPlaceSelected]);

  useEffect(() => {
    errorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!inputRef.current) return;
    if (autocompleteRef.current) return;

    const googleAny = (window as any)?.google;
    if (!googleAny?.maps?.places?.Autocomplete) {
      errorRef.current?.(t('searchLocationModal.googleMapsUnavailable'));
      return;
    }

    inputRef.current.focus();

    let observer: MutationObserver | null = null;

    const applyPacContainerStyles = () => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach((container) => {
        const el = container as HTMLElement;
        el.style.zIndex = '20000';
        el.style.position = 'fixed';
      });
    };

    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          const element = node as Element;
          if (!element.classList?.contains('pac-container')) continue;
          (element as HTMLElement).style.zIndex = '20000';
          (element as HTMLElement).style.position = 'fixed';
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    applyPacContainerStyles();
    window.setTimeout(applyPacContainerStyles, 100);

    autocompleteRef.current = new googleAny.maps.places.Autocomplete(inputRef.current, {
      types: ['geocode'],
      componentRestrictions: { country: 'ar' },
    });

    const listener = autocompleteRef.current.addListener?.('place_changed', () => {
      const place = autocompleteRef.current?.getPlace?.();

      if (!place?.geometry?.location) {
        errorRef.current?.(t('searchLocationModal.selectedPlaceNoLocation'));
        return;
      }

      let city = '';
      let state = '';
      let country = '';
      let postalCode = '';
      let streetName = '';
      let streetNumber = '';

      const components: any[] = place.address_components ?? [];
      for (const component of components) {
        const types: string[] = component?.types ?? [];

        if (types.includes('locality') || types.includes('administrative_area_level_2')) {
          city = component.long_name;
          continue;
        }
        if (types.includes('administrative_area_level_1')) {
          state = component.long_name;
          continue;
        }
        if (types.includes('country')) {
          country = component.long_name;
          continue;
        }
        if (types.includes('postal_code')) {
          postalCode = component.long_name;
          continue;
        }
        if (types.includes('route')) {
          streetName = component.long_name;
          continue;
        }
        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
      }

      const lat =
        typeof place.geometry.location.lat === 'function' ? place.geometry.location.lat() : 0;
      const lng =
        typeof place.geometry.location.lng === 'function' ? place.geometry.location.lng() : 0;

      const result: LocationResult = {
        location: { lat, lng },
        address: place.formatted_address ?? '',
        city,
        state,
        country,
        postalCode,
        streetName,
        streetNumber,
        shortAddress: [streetName, streetNumber].filter(Boolean).join(' '),
      };

      placeSelectedRef.current(result);
    });

    return () => {
      if (observer) observer.disconnect();
      if (listener?.remove) listener.remove();
      autocompleteRef.current = null;
    };
  }, [isLoaded, t]);

  const isDisabled = isLoading || !isLoaded || Boolean(mapsError);

  return (
    <TextField
      fullWidth
      size="small"
      inputRef={inputRef}
      placeholder={currentPlaceholder}
      disabled={isDisabled}
      error={Boolean(mapsError)}
    />
  );
};

export default SearchLocationInput;
export type { LocationResult };
